// UserTable.tsx
import { FC } from 'react';
import { Button, Flex, Input, Select, Table, TableProps, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import moment from 'moment/moment';

export type UserStatus = 'ACTIVE' | 'SUSPENDED';
export type UserRole = 'Admin' | 'Physician' | 'Technician';

export interface IUser {
  key: string; // you can map this to uuid/id in Admin when building results
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  lastLogin: string | null; // ISO / server string
  numberOfAssignments: number;
}

interface IProps {
  users: IUser[];
  page: number;
  pageSize: number;
  loading?: boolean;
  onTableChange: TableProps<IUser>['onChange'];

  // controls from parent (server-side) - optional when hideFilters is true
  search?: string;
  onSearchChange?: (v: string) => void;
  onRoleChange?: (v: 'All' | IUser['role']) => void;
  onStatusChange?: (v: 'All' | IUser['status']) => void;

  // hide the filter bar (when filters are in SubSideBar)
  hideFilters?: boolean;
}

const roleOptions = [
  { label: 'All Roles', value: 'All' },
  { label: 'Admin', value: 'Admin' },
  { label: 'Physician', value: 'Physician' },
  { label: 'Technician', value: 'Technician' },
];

const statusOptions = [
  { label: 'All Statuses', value: 'All' },
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Suspended', value: 'SUSPENDED' },
];

const UsersTable: FC<IProps> = ({
  users,
  page,
  pageSize,
  loading,
  onTableChange,
  search,
  onSearchChange,
  onRoleChange,
  onStatusChange,
  hideFilters,
}) => {
  // You can still keep these inline actions as UI only or later wire them
  const columns: ColumnsType<IUser> = [
    {
      title: 'Name',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: true, // server-side
      render: (_, record) => (
        <strong>{`${record.firstName} ${record.lastName}`}</strong>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      sorter: true,
      render: (role: UserRole) => <span>{role}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status: UserStatus) => (
        <Tag
          color={status === 'ACTIVE' ? 'green' : 'red'}
          style={{ textTransform: 'lowercase' }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      sorter: true,
      render: (text: string | null) => (
        <span>
          {text != null
            ? moment(text, 'YYYY-MM-DD HH:mm:ss').format('MMMM Do YYYY, h:mm A')
            : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Assigned',
      dataIndex: 'numberOfAssignments',
      key: 'numberOfAssignments',
      align: 'right',
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button type="link">Reset Password</Button>
          {/* These could call mutations later; currently UI only */}
          <Button type="link">
            {record.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}
          </Button>
        </>
      ),
    },
  ];

  return (
    <Flex vertical gap={24} style={{ width: '100%' }}>
      {!hideFilters && (
        <Flex align="center" gap={8} wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => onSearchChange?.(e.target.value)}
            style={{ minWidth: 280, maxWidth: 560 }}
          />

          <Select
            defaultValue="All"
            onChange={(val) => onRoleChange?.(val as 'All' | IUser['role'])}
            style={{ width: 180 }}
            options={roleOptions}
          />

          <Select
            defaultValue="All"
            onChange={(val) => onStatusChange?.(val as 'All' | IUser['status'])}
            style={{ width: 180 }}
            options={statusOptions}
          />

          <Flex style={{ marginLeft: 'auto' }}>
            <Button type="primary" icon={<PlusOutlined />}>
              Add User
            </Button>
          </Flex>
        </Flex>
      )}

      <Table<IUser>
        columns={columns}
        dataSource={users}
        rowKey={(r) => r.key}
        loading={loading}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize,
          total: users?.length, // OR usersPagination.count if you pass it in
          showSizeChanger: true,
          showTotal: (t, [s, e]) => `${s}-${e} of ${t}`,
        }}
      />
    </Flex>
  );
};

export default UsersTable;
